import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import Form from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import TagBox from 'devextreme-react/tag-box';
import ScrollView from 'devextreme-react/scroll-view';
import notify from 'devextreme/ui/notify';
import { getApiToken } from '../../utils/authUtils';
import './user-edit.scss';

const buildFormItems = () => ([
  { dataField: 'displayName', label: { text: 'Name' }, editorType: 'dxTextBox', isRequired: true, editorOptions: { stylingMode: 'filled' } },
  { dataField: 'mail', label: { text: 'E-Mail' }, editorType: 'dxTextBox', editorOptions: { stylingMode: 'filled' } },
  { dataField: 'status', label: { text: 'Status' }, editorType: 'dxTextBox', editorOptions: { stylingMode: 'filled' } },
  { dataField: 'allowCreateCompetition', label: { text: 'Wettkämpfe erstellen erlaubt' }, editorType: 'dxSwitch' },
  { dataField: 'isEntraUser', label: { text: 'Entra-ID Benutzer' }, editorType: 'dxSwitch' },
  { dataField: 'isDisabled', label: { text: 'Deaktiviert' }, editorType: 'dxSwitch' }
]);

const toPermissionOption = (p) => ({
  id: p.permissionId,
  text: p.value,
  detail: p.detail,
  group: p.groupName
});

const UserEditPage = () => {
  const { userId } = useParams();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const formItems = useMemo(buildFormItems, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getApiToken(instance, accounts);
      const userStore = createStore({
        key: 'userId',
        loadUrl: `${process.env.REACT_APP_serverEndpoint}/User/Users`,
        onBeforeSend: (method, ajaxOptions) => {
          ajaxOptions.headers = { Authorization: `Bearer ${token}` };
        }
      });

      const [userList, permsResponse, userPermsResponse] = await Promise.all([
        userStore.load({ filter: ['userId', '=', userId] }),
        fetch(`${process.env.REACT_APP_serverEndpoint}/User/AllPermissions`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_serverEndpoint}/User/UserPermissions?userId=${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (!permsResponse.ok) {
        throw new Error('Konnte Berechtigungen nicht laden');
      }
      if (!userPermsResponse.ok) {
        throw new Error('Konnte Benutzerberechtigungen nicht laden');
      }

      const user = userList?.[0];
      if (!user) {
        throw new Error('Benutzer nicht gefunden');
      }

      const perms = await permsResponse.json();
      const userPermIds = await userPermsResponse.json();

      setFormData(user);
      setPermissionOptions(perms.map(toPermissionOption));
      setSelectedPermissions(userPermIds);
    } catch (error) {
      notify(error.message || 'Laden fehlgeschlagen', 'error', 3000);
    } finally {
      setLoading(false);
    }
  }, [accounts, instance, userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onSave = useCallback(async () => {
    try {
      const token = await getApiToken(instance, accounts);

      const userPayload = new FormData();
      userPayload.append('key', userId);
      userPayload.append('values', JSON.stringify(formData));

      const updateResponse = await fetch(`${process.env.REACT_APP_serverEndpoint}/User/UpdateUser`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: userPayload
      });

      if (!updateResponse.ok) {
        const error = await updateResponse.text();
        throw new Error(error || 'Speichern fehlgeschlagen');
      }

      const permissionPayload = new FormData();
      permissionPayload.append('userId', userId);
      permissionPayload.append('permissionIds', JSON.stringify(selectedPermissions));

      const permResponse = await fetch(`${process.env.REACT_APP_serverEndpoint}/User/SetUserPermissions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: permissionPayload
      });

      if (!permResponse.ok) {
        const error = await permResponse.text();
        throw new Error(error || 'Berechtigungen speichern fehlgeschlagen');
      }

      notify('Benutzer gespeichert', 'success', 2000);
    } catch (error) {
      notify(error.message || 'Speichern fehlgeschlagen', 'error', 3000);
    }
  }, [accounts, formData, instance, selectedPermissions, userId]);

  return (
    <div className='view user-edit'>
      <ScrollView className='view-wrapper view-wrapper-user-edit view-wrapper-scroll'>
        <div className='toolbar'>
          <Button icon='arrowleft' stylingMode='text' onClick={() => navigate(-1)} />
          <div className='title'>{formData?.displayName || 'Benutzer'}</div>
          <div className='toolbar-spacer' />
          <Button icon='refresh' stylingMode='text' onClick={loadData} disabled={loading} />
          <Button text='Speichern' type='default' icon='save' onClick={onSave} disabled={loading} />
        </div>

        <div className='content'>
          <div className='panel'>
            <Form
              formData={formData}
              items={formItems}
              colCount={2}
              labelLocation='top'
              onFieldDataChanged={(e) => setFormData({ ...formData, [e.dataField]: e.value })}
              readOnly={loading}
            />
          </div>

          <div className='panel'>
            <div className='panel-title'>Berechtigungen</div>
            <TagBox
              dataSource={permissionOptions}
              value={selectedPermissions}
              valueExpr='id'
              displayExpr={(item) => item && item.detail}
              onValueChanged={(e) => setSelectedPermissions(e.value)}
              showSelectionControls
              searchEnabled
              placeholder='Berechtigungen auswählen'
              disabled={loading}
            />
          </div>
        </div>
      </ScrollView>
    </div>
  );
};

export default UserEditPage;
